import React from 'react';
import {
    Box,
    FormControl,
    FormLabel,
    Heading,
    Input,
    Text,
    VStack
} from '@chakra-ui/react';

const BUILT_IN_VARIABLES = new Set([
    'fullDate',
    'fullDateWithoutYear',
    'currentTime',
    'day',
    'month',
    'year',
    'dayOfWeek'
]);

export const extractMustacheVariables = (...texts) => {
    const variables = new Set();
    const variablePattern = /<<\s*([A-Za-z0-9_.-]+)\s*>>/g;

    for (const text of texts) {
        if (!text) continue;

        let match;
        while ((match = variablePattern.exec(String(text))) !== null) {
            if (!BUILT_IN_VARIABLES.has(match[1])) {
                variables.add(match[1]);
            }
        }
    }

    return Array.from(variables).sort();
};

const setNestedValue = (target, path, value) => {
    const parts = path.split('.');
    let current = target;

    parts.forEach((part, index) => {
        if (index === parts.length - 1) {
            current[part] = value;
            return;
        }

        current[part] = current[part] || {};
        current = current[part];
    });
};

export const buildCallData = (variableValues) => {
    return Object.entries(variableValues).reduce((data, [key, value]) => {
        setNestedValue(data, key, value);
        return data;
    }, {});
};

export const getCallVariableStorageKey = (assistantId) => `call_me_variables_${assistantId || 'default'}`;
export const getCallPhoneStorageKey = (assistantId) => `call_me_phone_numbers_${assistantId || 'default'}`;

const readStoredJson = (key, label) => {
    try {
        return JSON.parse(localStorage.getItem(key) || '{}');
    } catch (error) {
        console.warn(`Failed to read stored ${label}:`, error);
        return {};
    }
};

export const readStoredCallVariables = (assistantId) => {
    if (!assistantId) return {};
    return readStoredJson(getCallVariableStorageKey(assistantId), 'call variables');
};

export const readStoredCallPhones = (assistantId) => {
    if (!assistantId) return {};
    return readStoredJson(getCallPhoneStorageKey(assistantId), 'call phone numbers');
};

export const saveStoredCallVariables = (assistantId, values) => {
    if (!assistantId) return;
    localStorage.setItem(getCallVariableStorageKey(assistantId), JSON.stringify(values));
};

export const saveStoredCallPhones = (assistantId, values) => {
    if (!assistantId) return;
    localStorage.setItem(getCallPhoneStorageKey(assistantId), JSON.stringify(values));
};

const CallSetupFields = ({
    children,
    colorMode,
    variables = [],
    variableValues = {},
    onVariableChange,
    showParticipantPhone = false,
    participantPhone = '',
    onParticipantPhoneChange,
    participantPhoneLabel = 'Call Participant Phone Number',
    participantPhonePlaceholder = 'Enter phone number'
}) => {
    const hasChildren = Boolean(children);
    const fieldTextColor = colorMode === 'light' ? 'gray.600' : 'gray.300';

    return (
        <>
            {children}

            {showParticipantPhone && (
                <Box mt={hasChildren ? 6 : 0}>
                    <FormControl>
                        <FormLabel>{participantPhoneLabel}</FormLabel>
                        <Input
                            value={participantPhone}
                            placeholder={participantPhonePlaceholder}
                            onChange={(e) => onParticipantPhoneChange?.(e.target.value)}
                        />
                    </FormControl>
                </Box>
            )}

            {variables.length > 0 && (
                <Box mt={hasChildren || showParticipantPhone ? 6 : 0}>
                    <Heading size="sm" mb={2}>Call Variables</Heading>
                    <Text fontSize="sm" color={fieldTextColor} mb={4}>
                        These values personalize this test call and fill matching prompt variables.
                    </Text>
                    <VStack spacing={3} align="stretch">
                        {variables.map((variable) => (
                            <FormControl key={variable}>
                                <FormLabel>{variable}</FormLabel>
                                <Input
                                    value={variableValues[variable] || ''}
                                    placeholder={`Enter ${variable}`}
                                    onChange={(e) => onVariableChange?.(variable, e.target.value)}
                                />
                            </FormControl>
                        ))}
                    </VStack>
                </Box>
            )}
        </>
    );
};

export default CallSetupFields;
